import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PRFlowchart from './PRFlowchart'; // Assuming this component exists
import { Box, Button, Typography, RadioGroup, FormControlLabel, Radio, Alert, Stepper, Step, StepLabel, Paper, CircularProgress, List, ListItem, ListItemText } from '@mui/material'; // Import MUI components
import axiosInstance from '../api/axiosInstance'; // Assuming you have axiosInstance configured

// --- Component Implementation ---

function PRInteractiveGuide() {
  const [mode, setMode] = useState(null); // 'explore' or 'guided'
  const [questionnaireData, setQuestionnaireData] = useState(null); // To store fetched JSON data
  const [startQuestionId, setStartQuestionId] = useState(null); // To store the ID of the first question
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [history, setHistory] = useState([]); // To track question path for 'Back' button
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const totalQuestionsEstimate = 10; // Approximate for stepper display, might need adjustment

  // --- Data Fetching ---
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      setQuestionnaireData(null); // Clear previous data on refetch attempt
      setStartQuestionId(null);
      setCurrentQuestionId(null);
      try {
        // Fetch data from the new backend endpoint
        const response = await axiosInstance.get('/api/pr-pathways-guided');
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          setQuestionnaireData(response.data);
          // Assuming the first item in the array is the starting question
          const initialId = response.data[0].id;
          setStartQuestionId(initialId);
          // Set currentQuestionId only if mode is already 'guided' or becomes 'guided' later
          // Avoid setting it prematurely if user starts in 'explore' mode
          if (mode === 'guided') {
              setCurrentQuestionId(initialId);
          }
        } else {
          console.error("Received invalid data format:", response.data);
          setError("Received invalid questionnaire data format from the server.");
        }
      } catch (err) {
        console.error("Error fetching questionnaire data:", err);
        const errorMsg = err.response?.data?.message || err.message || "Failed to load the questionnaire.";
        setError(`${errorMsg} Please try again later.`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [mode]);

  // --- Derived State ---
  const currentQuestionData = useMemo(() => {
    if (!questionnaireData || !currentQuestionId) return null;
    // Find the question data based on the current ID
    return questionnaireData.find(q => q.id === currentQuestionId);
  }, [questionnaireData, currentQuestionId]);

  // --- Event Handlers ---

  const handleModeSelection = (selectedMode) => {
    setMode(selectedMode);
    // Reset guided mode state if switching away from it
    if (selectedMode !== 'guided' && startQuestionId) {
       handleRestart(false); // Reset state but keep mode selection
    }
    // If switching TO guided and data is loaded, set the initial question ID
    if (selectedMode === 'guided' && startQuestionId && !currentQuestionId) {
        setCurrentQuestionId(startQuestionId);
        setShowResults(false); // Ensure results aren't shown initially
        setAnswers({}); // Clear answers when starting guided mode
        setHistory([]); // Clear history
    }
  };

  // Centralized branching logic function
  const getNextQuestionId = useCallback((currentId, answer, allAnswers) => {
      // Find the current question's definition from the fetched data
      const questionDef = questionnaireData?.find(q => q.id === currentId);
      // Check if the question definition exists and has a 'next' property
      if (!questionDef || typeof questionDef.next !== 'string') {
          console.warn(`Question definition or 'next' property missing for ID: ${currentId}`);
          return 'other_options_info'; // Fallback if definition is missing or 'next' isn't a string
      }

      // If 'next' is a direct string ID (simple cases from JSON, not requiring logic)
      if (!questionDef.next.startsWith('logic_')) {
          return questionDef.next;
      }

      // --- Complex Logic Implementation (based on previous hardcoded logic) ---
      // This switch statement replicates the branching logic that depends on answers.
      switch (currentId) {
          case 'start':
              if (answer.includes("skilled work experience")) return 'work_experience_location';
              if (answer.includes("family")) return 'family_sponsor_check';
              if (answer.includes("studying")) return 'student_status';
              if (answer.includes("job offer")) return 'job_offer_skilled';
              if (answer.includes("specific province")) return 'pnp_interest';
              return 'other_options_info';

          case 'work_experience_location':
              if (answer.includes("Inside Canada") || answer.includes("Both")) return 'canadian_experience_duration';
              if (answer.includes("Outside Canada")) return 'foreign_experience_duration';
              return 'other_options_info';

          case 'canadian_experience_duration':
              if (answer.includes("1 year or more")) {
                  // Check the *initial* answer to 'start' if available
                  if (allAnswers['start']?.includes("Both")) return 'language_test_cec_fswp';
                  return 'language_test_cec';
              }
              // If less than 1 year Canadian, check how they answered 'work_experience_location'
              if (allAnswers['work_experience_location']?.includes("Both")) return 'foreign_experience_duration';
              // If they only had Canadian experience (less than 1 year), need to check foreign
              return 'foreign_experience_check';

          case 'foreign_experience_duration':
              if (answer.includes("trade")) return 'language_test_fstp';
              if (answer.includes("1 year") || answer.includes("2 years or more (not")) {
                  // Check if they also have Canadian experience possibility
                  if (allAnswers['start']?.includes("Both") || allAnswers['canadian_experience_duration']?.includes("Less than 1 year")) return 'language_test_cec_fswp';
                  return 'language_test_fswp';
              }
              // If less than 1 year foreign, but they had Canadian experience > 1 year
              if (allAnswers['canadian_experience_duration']?.includes("1 year or more")) return 'language_test_cec';
              return 'other_options_info';

          case 'foreign_experience_check': // This question implies Canadian experience was < 1 year
              if (answer.includes("trade")) return 'language_test_fstp';
              if (answer.includes("1 year") || answer.includes("2 years or more (not")) return 'language_test_fswp';
              return 'other_options_info';

          case 'language_test_cec_fswp':
               return answer === "Yes" ? 'language_level_cec_fswp' : 'language_test_required_outcome';

          case 'language_level_cec_fswp':
              // Determine potential eligibility based on *previous* answers
              const hasMinCEC_cf = allAnswers['canadian_experience_duration']?.includes("1 year or more");
              // Check both possible preceding questions for foreign experience
              const hasMinFSWP_cf = allAnswers['foreign_experience_duration']?.includes("1 year") || allAnswers['foreign_experience_duration']?.includes("2 years or more (not") ||
                                  allAnswers['foreign_experience_check']?.includes("1 year") || allAnswers['foreign_experience_check']?.includes("2 years or more (not");

              if (answer.includes("CLB 7+")) {
                  if (hasMinCEC_cf && hasMinFSWP_cf) return 'ee_cec_fswp_eligible_outcome';
                  if (hasMinCEC_cf) return 'cec_eligible_outcome';
                  if (hasMinFSWP_cf) return 'fswp_education_check'; // Go to next FSWP check
              }
              if (answer.includes("CLB 5+")) {
                  // Assumes TEER 2/3 if CLB 5-6. A real app might need TEER level question.
                  if (hasMinCEC_cf) return 'cec_eligible_teer23_outcome';
              }
              // If FSWP was possible but language < CLB 7, or CEC < CLB 5
              return 'language_improvement_outcome';

          case 'language_test_cec':
              return answer === "Yes" ? 'cec_language_level' : 'language_test_required_outcome';

          case 'cec_language_level':
              if (answer.includes("CLB 7+")) return 'cec_eligible_outcome';
              if (answer.includes("CLB 5+")) return 'cec_eligible_teer23_outcome';
              return 'language_improvement_outcome';

          case 'language_test_fswp':
               return answer === "Yes" ? 'fswp_language_level' : 'language_test_required_outcome';

          case 'fswp_language_level':
               return answer === "Yes" ? 'fswp_education_check' : 'language_improvement_outcome';

          case 'fswp_education_check':
              return answer === "Yes" ? 'fswp_points_check' : 'fswp_eca_required_outcome';

          case 'fswp_points_check':
              return answer.includes("Yes") ? 'fswp_funds_check' : 'fswp_points_low_outcome';

          case 'fswp_funds_check':
              return answer === "Yes" ? 'fswp_eligible_outcome' : 'fswp_funds_required_outcome';

          case 'language_test_fstp':
              return answer === "Yes" ? 'fstp_language_level' : 'language_test_required_outcome';

          case 'fstp_language_level':
              return answer === "Yes" ? 'fstp_offer_or_cert_check' : 'language_improvement_outcome';

          case 'fstp_offer_or_cert_check':
              return answer.includes("Yes") ? 'fstp_funds_check' : 'fstp_offer_cert_required_outcome';

          case 'fstp_funds_check':
              return answer === "Yes" ? 'fstp_eligible_outcome' : 'fstp_funds_required_outcome';

          case 'family_sponsor_check':
              if (answer.includes("Spouse") || answer.includes("Partner")) return 'family_sponsor_spousal_outcome';
              if (answer.includes("Parent")) return 'family_sponsor_parent_outcome';
              if (answer.includes("Child")) return 'family_sponsor_child_outcome';
              return 'family_sponsor_other_outcome';

          case 'student_status':
              if (answer.includes("Currently studying")) return 'student_current_outcome';
              if (answer.includes("Recently graduated")) return 'student_graduated_pgwp_outcome';
              if (answer.includes("Planning")) return 'student_planning_outcome';
              return 'student_other_outcome';

          case 'job_offer_skilled':
              if (answer.includes("TEER 0, 1, 2, or 3")) return 'job_offer_details';
              if (answer.includes("TEER 4 or 5")) return 'job_offer_pnp_check';
              return 'job_offer_invalid_outcome';

          case 'job_offer_details':
               return answer.includes("Yes") ? 'job_offer_lmia_check' : 'job_offer_invalid_outcome';

          case 'job_offer_lmia_check':
               return answer.includes("Yes") ? 'job_offer_valid_ee_pnp_outcome' : 'job_offer_no_lmia_outcome';

          // Cases where 'next' is directly specified in JSON (no complex logic needed here)
          // case 'job_offer_pnp_check': return 'pnp_research_outcome'; // Handled by direct 'next' value
          // case 'pnp_interest': return 'pnp_research_outcome'; // Handled by direct 'next' value

          default:
              console.warn(`No specific logic found for current question ID: ${currentId}. Falling back.`);
              return 'other_options_info'; // Fallback for any unhandled case
      }

  }, [questionnaireData]); // Dependency: questionnaireData is needed for the logic

  const handleAnswer = (event) => {
    const { value } = event.target;
    const updatedAnswers = { ...answers, [currentQuestionId]: value };
    setAnswers(updatedAnswers);

    if (currentQuestionData && questionnaireData) {
      // Use the centralized logic function to get the next ID
      const nextId = getNextQuestionId(currentQuestionId, value, updatedAnswers);
      const nextQuestion = questionnaireData.find(q => q.id === nextId);

      if (nextQuestion?.outcome) {
        setHistory([...history, currentQuestionId]); // Add current question to history before showing results
        setCurrentQuestionId(nextId);
        setShowResults(true);
      } else if (nextQuestion) {
        setHistory([...history, currentQuestionId]); // Add current question to history
        setCurrentQuestionId(nextId);
        setShowResults(false); // Ensure results are hidden if moving to another question
      } else {
        // Fallback if nextId is invalid or doesn't correspond to a question/outcome
        console.error("Invalid next question ID determined or question not found:", nextId);
        const fallbackOutcome = questionnaireData.find(q => q.id === 'other_options_info');
        if (fallbackOutcome) {
            setHistory([...history, currentQuestionId]);
            setCurrentQuestionId('other_options_info');
            setShowResults(true);
        } else {
             setError("An unexpected error occurred navigating the questionnaire.");
             setShowResults(false);
        }
      }
    } else {
       console.error("Cannot handle answer: questionnaire data or current question definition is missing.");
       setError("An error occurred processing your answer.");
    }
  };

  const handleBack = () => {
    const previousQuestionId = history.length > 0 ? history[history.length - 1] : null;

    if (previousQuestionId) { // Covers both going back from results and from another question
        const newAnswers = { ...answers };
        // Clear the answer for the question we are navigating back TO.
        // This ensures the RadioGroup's onChange fires even if the same option is clicked again.
        delete newAnswers[previousQuestionId];
        setAnswers(newAnswers); // Update answers *before* changing question ID

        setCurrentQuestionId(previousQuestionId);
        setShowResults(false); // Ensure results are hidden when going back
        setHistory(history.slice(0, -1)); // Remove the question/result node we just navigated back from
    } else {
        // If history is empty (at the first question), go back to mode selection
        setMode(null);
        handleRestart(false); // Reset state but keep fetched data
    }
  };


  const handleRestart = (resetMode = true) => {
    setAnswers({});
    setCurrentQuestionId(startQuestionId); // Reset to the fetched start question ID
    setHistory([]);
    setShowResults(false);
    setError(null); // Clear errors on restart
    if (resetMode) {
      setMode(null); // Optionally go back to mode selection
    }
  };

   const handleSwitchToExplore = () => {
    setMode('explore');
    // Optionally reset guided state if desired when switching
    // handleRestart(false); // Uncomment if you want to clear answers/history when switching
  };

  // --- Render Functions ---

  const renderInitialSelection = () => (
    <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, textAlign: 'center', borderTop: '3px solid var(--color-accent-red)', border: '1px solid var(--color-border)' }}>
      <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>How familiar are you with Canadian PR pathways?</Typography>
      <Typography paragraph sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: '0.9rem', sm: '1rem' } }}>Choose how you'd like to explore:</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
        {/* Outlined uses primary blue by default */}
        <Button variant="outlined" size="large" onClick={() => handleModeSelection('explore')}>
          Explore Flowchart
        </Button>
        {/* Contained uses accent red by default */}
        <Button variant="contained" size="large" onClick={() => handleModeSelection('guided')} disabled={isLoading || !!error}>
          Guide Me
        </Button>
      </Box>
       <Alert severity="info" sx={{ mt: { xs: 2, sm: 3, md: 4 }, textAlign: 'left', fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
         <strong>Disclaimer:</strong> This tool provides a preliminary assessment based on general information and common pathways. It is NOT immigration advice. Rules change frequently. Always consult the official IRCC website and relevant provincial sites, or a qualified immigration professional for accurate, up-to-date, and personalized guidance.
       </Alert>
    </Paper>
  );

  const renderQuestionnaire = () => {
    // Handle case where results should be shown but data might still be loading/error
     if (showResults && currentQuestionData) {
       return renderResults();
     }
     // Handle case where currentQuestionData is null even if not showing results (e.g., during initial load/error)
     if (!currentQuestionData) {
         // Avoid showing error if it's just the initial loading state before mode selection
         if (mode === 'guided' && !isLoading) {
            return <Alert severity="error">Error: Current question data not available. Please restart.</Alert>;
         }
         return null; // Don't render anything if not in guided mode or still loading initially
     }


    const currentStepIndex = history.length;

    return (
      <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, borderTop: '3px solid var(--color-accent-red)', border: '1px solid var(--color-border)' }}>
        <Stepper
          activeStep={currentStepIndex}
          alternativeLabel
          sx={{
            mb: { xs: 2, sm: 3 },
            overflowX: 'auto', // Allow horizontal scrolling on small screens
            pb: 1, // Add some padding at the bottom for the scrollbar
            '& .MuiStep-root': { px: { xs: 0.2, sm: 0.5 } }, // Reduce padding between steps slightly
            '& .MuiStepLabel-root': { minWidth: 'auto' }, // Allow labels to shrink if needed (though we hide them)
          }}
        >
          {[...Array(totalQuestionsEstimate)].map((_, index) => (
            // Keep StepLabel empty as before, the numbers are handled by the Stepper itself with alternativeLabel
            <Step key={index}><StepLabel></StepLabel></Step>
          ))}
        </Stepper>

        <Typography variant="h6" gutterBottom sx={{ mb: { xs: 1, sm: 1.5 }, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          {currentQuestionData.text}
        </Typography>

        {currentQuestionData.info && (
          <Alert severity="info" sx={{ mb: { xs: 1, sm: 2 }, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>{currentQuestionData.info}</Alert>
        )}

        <RadioGroup
          aria-labelledby={`question-${currentQuestionId}`}
          name={currentQuestionId}
          value={answers[currentQuestionId] || ''}
          onChange={handleAnswer}
          sx={{ mb: { xs: 1.5, sm: 2 } }}
        >
          {currentQuestionData.options.map((option) => (
            <FormControlLabel key={option} value={option} control={<Radio />} label={option} sx={{ alignItems: 'center', mb: 0.5 }} />
          ))}
        </RadioGroup>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: 1 }}>
          {/* Reverted to outlined to use new red border/black text style */}
          <Button variant="outlined" onClick={handleBack} disabled={history.length === 0 && !showResults}>
            Back
          </Button>
           {/* Reverted to outlined to use new red border/black text style */}
           <Button variant="outlined" onClick={handleSwitchToExplore}>
             Switch to Flowchart
           </Button>
        </Box>
      </Paper>
    );
  };

 const renderResults = () => {
     // We assume if showResults is true, currentQuestionData is the outcome node
     if (!currentQuestionData || !currentQuestionData.outcome) {
       return <Alert severity="error">Error: Could not display results. Please restart.</Alert>;
     }

     return (
       <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, borderTop: '3px solid var(--color-accent-red)', border: '1px solid var(--color-border)' }}>
         <Typography variant="h5" gutterBottom sx={{ mb: { xs: 1, sm: 2 }, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Preliminary Pathway Assessment</Typography>

         {currentQuestionData.pathways && currentQuestionData.pathways.length > 0 && (
           <>
             <Typography paragraph sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>Based on your answers, you might be eligible for or should investigate the following pathway(s):</Typography>
             {/* Apply list styling via sx prop */}
             <List dense sx={{ mb: { xs: 1, sm: 2 }, pl: 2, listStyleType: 'disc', '& .MuiListItem-root': { display: 'list-item', p: 0, pl: 1 } }}>
               {currentQuestionData.pathways.map((pathway) => (
                 <ListItem key={pathway} sx={{ display: 'list-item', p: 0, pl: 1}}> {/* Keep specific item style if needed, though parent sx might cover it */}
                   <ListItemText primary={pathway} primaryTypographyProps={{ fontWeight: 'medium', fontSize: { xs: '0.9rem', sm: '1rem' } }} />
                 </ListItem>
               ))}
             </List>
           </>
         )}

         {/* Render message with potential line breaks */}
         <Typography paragraph sx={{ mt: { xs: 0.5, sm: 1 }, whiteSpace: 'pre-line', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            {currentQuestionData.message}
         </Typography>


         <Alert severity="warning" sx={{ mt: { xs: 2, sm: 3 }, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
           <strong>Important Disclaimer:</strong> This is a simplified assessment for informational purposes only and is NOT immigration advice. Eligibility criteria are complex and change often. This tool does not guarantee eligibility. Always consult official government sources (IRCC, provincial websites) or a qualified immigration professional before making decisions.
         </Alert>

         <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: 1 }}>
           {/* Reverted to outlined to use new red border/black text style */}
           <Button
               variant="outlined"
               onClick={handleBack}
               disabled={history.length === 0} // Can always go back from results if history exists
           >
               Back to Last Question
           </Button>
          {/* Contained uses accent red - Keep this one as primary action */}
          <Button variant="contained" onClick={() => handleRestart(true)}>
            Start Over
          </Button>
          {/* Reverted to outlined to use new red border/black text style */}
          <Button variant="outlined" onClick={handleSwitchToExplore}>
            View Flowchart
          </Button>
         </Box>
       </Paper>
     );
  };


  // --- Main Component Render Logic ---

  // Show loading indicator while fetching initial data
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', p: { xs: 1, sm: 2 } }}>
        <CircularProgress />
        <Typography sx={{ ml: 2, fontSize: { xs: '0.9rem', sm: '1rem' } }}>Loading Guide...</Typography>
      </Box>
    );
  }

  // Show error message if fetching failed
  if (error) {
    return (
       <Box sx={{ maxWidth: '800px', margin: 'auto', mt: { xs: 1, sm: 2 }, p: { xs: 1, sm: 0 } }}>
         <Alert severity="error" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
           {error}
           {/* Provide a way to retry or reload */}
           <Button onClick={() => window.location.reload()} sx={{ ml: 2 }} size="small">Reload Page</Button>
         </Alert>
       </Box>
    );
  }

  // Render the main UI once data is loaded and no errors
  return (
    <Box className="interactive-guide-container" sx={{ maxWidth: '800px', margin: 'auto', mt: { xs: 1, sm: 2 }, p: { xs: 1, sm: 0 } }}>
      {!mode && renderInitialSelection()}

      {mode === 'explore' && (
          <>
            {/* Allow switching back to guided mode - use outlined for secondary action */}
            <Button variant="outlined" onClick={() => handleModeSelection('guided')} sx={{ mb: { xs: 1, sm: 2 } }} disabled={!questionnaireData}>Switch to Guided Mode</Button>
            <PRFlowchart />
          </>
      )}

      {/* Render questionnaire/results only if data is loaded and mode is 'guided' */}
      {mode === 'guided' && questionnaireData && currentQuestionId && renderQuestionnaire()}

    </Box>
  );
}

export default PRInteractiveGuide;

// Removed inline style block - styles are handled via sx prop or App.css